import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fetchExperiences } from "src/actions/ExperienceActions";
import { fetchHomes, fetchPropertiesAsync } from "src/actions/HomeActions";
import { fetch_customers_async } from "src/actions/customer.actions";
import { fetch_transactions_async } from "src/actions/transaction.actions";
import { async_fetch_events } from "src/actions/event.action";
import { fetchHostBookings, formatDay, isPast, kindLabel } from "src/utils/bookings";
import { conversationContext, fetchConversations } from "src/utils/chat";

// The host's overview: what needs attention now (upcoming guests, unread
// messages) and how the business is doing.

const safeArr = (value) => (Array.isArray(value) ? value : []);
const isApproved = (listing) => listing?.approved === true || listing?.isApproved === true || listing?.status === "approved";

const KIND_ICON = { home: "fas fa-home", hotel: "fas fa-hotel", experience: "fas fa-compass", event: "fas fa-ticket-alt" };

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

/** Bookings per month for the last 12 months, by start date. */
function bookingsPerMonth(bookings) {
  const now = new Date();
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
    return { key: `${d.getFullYear()}-${d.getMonth()}`, month: d.toLocaleDateString([], { month: "short" }), bookings: 0 };
  });
  const index = Object.fromEntries(months.map((m, i) => [m.key, i]));
  bookings.forEach((b) => {
    if (!b.start) return;
    const d = new Date(b.start);
    const i = index[`${d.getFullYear()}-${d.getMonth()}`];
    if (i !== undefined) months[i].bookings += 1;
  });
  return months;
}

const Stat = ({ icon, label, value, hint, to }) => (
  <Link to={to} className="hd-card hd-stat">
    <span className="hd-stat-icon">
      <i className={icon} />
    </span>
    <span className="hd-stat-label">{label}</span>
    <strong className="hd-stat-value">{value}</strong>
    {hint && <span className="hd-stat-hint">{hint}</span>}
  </Link>
);

const Dashboard = ({
  fetchExperiences,
  fetchHomes,
  fetch_customers_async,
  fetch_transactions_async,
  async_fetch_events,
  fetchPropertiesAsync,
  experiences,
  homes,
  properties,
  events,
  customers,
  transactions,
  host,
}) => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);

  useEffect(() => {
    fetchExperiences();
    async_fetch_events();
    fetchHomes();
    fetchPropertiesAsync();
    fetch_customers_async();
    fetch_transactions_async();
    fetchHostBookings()
      .then(({ items }) => setBookings(items))
      .finally(() => setLoadingBookings(false));
    fetchConversations()
      .then((data) => {
        setConversations(data.results || []);
        setUnreadTotal(data.unread_total || 0);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listings = useMemo(() => {
    const groups = [
      { key: "homes", label: "Homes", icon: "fas fa-home", items: safeArr(homes), to: "/app/homes" },
      { key: "hotels", label: "Hotels", icon: "fas fa-hotel", items: safeArr(properties), to: "/app/properties" },
      { key: "experiences", label: "Experiences", icon: "fas fa-compass", items: safeArr(experiences), to: "/app/experiences" },
      { key: "events", label: "Events", icon: "fas fa-ticket-alt", items: safeArr(events), to: "/app/events" },
    ];
    return groups.map((g) => ({ ...g, approved: g.items.filter(isApproved).length }));
  }, [homes, properties, experiences, events]);

  const totalListings = listings.reduce((sum, g) => sum + g.items.length, 0);
  const approvedListings = listings.reduce((sum, g) => sum + g.approved, 0);

  const upcoming = useMemo(
    () =>
      bookings
        .filter((b) => !isPast(b) && b.status !== "expired" && b.status !== "declined")
        .sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0)),
    [bookings],
  );

  const thisMonth = useMemo(() => {
    const now = new Date();
    return bookings.filter((b) => {
      const d = new Date(b.start || 0);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [bookings]);

  const chart = useMemo(() => bookingsPerMonth(bookings), [bookings]);
  const earnings = Math.abs(Number(transactions) || 0);
  const firstName = host?.User_ID?.first_name || host?.User_ID?.username || "";

  return (
    <>
      <Helmet>
        <title>Overview | Tubayo Host</title>
      </Helmet>
      <div className="hd-page">
        <div className="hd-page-header">
          <div>
            <h1>
              {greeting()}
              {firstName ? `, ${firstName}` : ""}
            </h1>
            <p>Here's what's happening across your listings.</p>
          </div>
          <Link to="/app/bookings" className="hd-btn">
            <i className="far fa-calendar-check" /> View bookings
          </Link>
        </div>

        <div className="hd-stats">
          <Stat
            icon="far fa-calendar-check"
            label="Upcoming bookings"
            value={loadingBookings ? "…" : upcoming.length}
            hint={upcoming[0] ? `Next: ${formatDay(upcoming[0].start)}` : "Nothing scheduled"}
            to="/app/bookings"
          />
          <Stat
            icon="fas fa-chart-line"
            label="Bookings this month"
            value={loadingBookings ? "…" : thisMonth}
            hint={`${bookings.length} all time`}
            to="/app/bookings?tab=all"
          />
          <Stat
            icon="far fa-comment-dots"
            label="Unread messages"
            value={unreadTotal}
            hint={unreadTotal ? "Guests are waiting for a reply" : "You're all caught up"}
            to="/app/messages"
          />
          <Stat
            icon="fas fa-home"
            label="Active listings"
            value={approvedListings}
            hint={`${totalListings - approvedListings} awaiting review`}
            to="/app/homes"
          />
        </div>

        <div className="hd-grid-2">
          <section className="hd-card hd-section">
            <div className="hd-section-header">
              <div>
                <h3>Bookings per month</h3>
                <p className="hd-muted">Last 12 months, by check-in date</p>
              </div>
              <div className="hd-earnings">
                <span className="hd-muted">Total earnings</span>
                <strong>
                  USD {earnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </strong>
              </div>
            </div>
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#F0F0F0" />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#717171" }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#717171" }} />
                  <Tooltip cursor={{ fill: "#F7F7F7" }} contentStyle={{ borderRadius: 10, border: "1px solid #EBEBEB" }} />
                  <Bar dataKey="bookings" fill="#222222" radius={[6, 6, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="hd-card hd-section">
            <div className="hd-section-header">
              <h3>Your listings</h3>
              <span className="hd-muted">
                {customers?.length || 0} customer{customers?.length === 1 ? "" : "s"}
              </span>
            </div>
            {listings.map((g) => (
              <Link key={g.key} to={g.to} className="hd-list-row">
                <span className="hd-stat-icon small">
                  <i className={g.icon} />
                </span>
                <span className="hd-cell-title" style={{ flex: 1 }}>
                  <strong>{g.label}</strong>
                  <span>
                    {g.approved} live · {g.items.length - g.approved} in review
                  </span>
                </span>
                <strong>{g.items.length}</strong>
              </Link>
            ))}
          </section>
        </div>

        <div className="hd-grid-2">
          <section className="hd-card hd-section">
            <div className="hd-section-header">
              <h3>Upcoming check-ins</h3>
              <Link to="/app/bookings" className="hd-link">
                See all
              </Link>
            </div>
            {loadingBookings ? (
              <div className="hd-skeleton" />
            ) : upcoming.length === 0 ? (
              <p className="hd-muted">No upcoming guests yet.</p>
            ) : (
              upcoming.slice(0, 5).map((b) => (
                <button key={b.key} type="button" className="hd-list-row" onClick={() => navigate(`/app/bookings?type=${b.kind}`)}>
                  <span className="hd-date-chip">
                    <small>{new Date(b.start).toLocaleDateString([], { month: "short" })}</small>
                    <strong>{new Date(b.start).getDate()}</strong>
                  </span>
                  <span className="hd-cell-title" style={{ flex: 1, minWidth: 0 }}>
                    <strong>{b.guest.name}</strong>
                    <span>
                      <i className={KIND_ICON[b.kind]} /> {b.listing}
                    </span>
                  </span>
                  <span className="hd-muted" style={{ fontSize: 13 }}>
                    {kindLabel(b.kind)}
                  </span>
                </button>
              ))
            )}
          </section>

          <section className="hd-card hd-section">
            <div className="hd-section-header">
              <h3>Recent messages</h3>
              <Link to="/app/messages" className="hd-link">
                Open inbox
              </Link>
            </div>
            {conversations.length === 0 ? (
              <p className="hd-muted">When guests message you, conversations appear here.</p>
            ) : (
              conversations.slice(0, 4).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="hd-list-row"
                  onClick={() => navigate(`/app/messages?conversation=${c.id}`)}
                >
                  {c.other_party.photo ? (
                    <img src={c.other_party.photo} alt="" className="hd-row-avatar" />
                  ) : (
                    <span className="hd-avatar-initial hd-row-avatar">{c.other_party.name.charAt(0).toUpperCase()}</span>
                  )}
                  <span className="hd-cell-title" style={{ flex: 1, minWidth: 0 }}>
                    <strong>{c.other_party.name}</strong>
                    <span className="hd-ellipsis">
                      {conversationContext(c)} · {c.last_message_preview}
                    </span>
                  </span>
                  {c.unread_count > 0 && <span className="hd-badge">{c.unread_count}</span>}
                </button>
              ))
            )}
          </section>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  experiences: state.experiences.experience,
  homes: state.homes.homes,
  customers: state.customers.customers,
  transactions: state.transactionReducers.transactions,
  events: state.eventReducer.events,
  properties: state.homes.hotels,
  host: state.edit_host?.host,
});

export default connect(mapStateToProps, {
  fetchExperiences,
  fetchHomes,
  fetch_customers_async,
  fetch_transactions_async,
  async_fetch_events,
  fetchPropertiesAsync,
})(Dashboard);
